import axios from '../../../common/utils/interceptor';
import React, { useEffect, useState } from 'react';
import { apiUrls, routeMappings } from '../../../constants';
import { ForgotPasswordForm } from '../ForgotPasswordForm';
import { GAPageViewMappings, GARouteMappings } from '../../../constants/gaMappings';
import { getCookie } from '../../../common/utils/cookies';
import { getLocalizedKeyByErrorMessage } from '../../utils/errorMessage';
import { sendPageViewDetails } from '../../../common/utils/gaEvents';
import { UserName } from '../../interfaces/AuthInputFields';
import { useTranslation } from 'react-i18next';
import { validateInputChangeMfsSignUpField } from '../../utils/validation';

/**
 * Container Component for MFS Forgot Password with input username
 */
export const MfsForgotPasswordPage = (): JSX.Element => {
  /** Page View GA method */
  useEffect(() => {
    sendPageViewDetails(GARouteMappings.SHL360_FORGOT_PASSWORD, GAPageViewMappings.SHL360_FORGOT_PASSWORD);
  }, []);

  const { t } = useTranslation();
  const isAccountLocked = window.location.hash.includes(routeMappings['ACCOUNT_LOCKED']) ? true : undefined;

  /** state for Forgot Password fields */
  const [forgotPasswordDetails, setForgotPasswordDetails] = useState<UserName>({
    username: '',
    usernameHelperText: '',
  });

  /** state for info/error alert */
  const [alert, setAlert] = useState<{ state: 'error' | 'info'; message: string }>({
    state: 'info',
    message: '',
  });

  const [loading, setLoading] = useState<boolean>(false);

  /** method for input change handler */
  const inputChangeHandler = (name: keyof UserName) => (event: React.ChangeEvent<{ value: string }>): void => {
    const value = event.target.value;
    const valueWithValidation = validateInputChangeMfsSignUpField(name, value);

    setForgotPasswordDetails({ ...forgotPasswordDetails, ...valueWithValidation });
  };

  /** method for forgot password api */
  const forgotPassword = async (): Promise<void> => {
    setLoading(true);
    try {
      const request = {
        locale: getCookie('locale_player') || 'EN_US',
        name: forgotPasswordDetails.username,
      };

      const response = await axios.post(apiUrls.RESET_PASSWORD, request);
      if (response.data.IsError) {
        setAlert({
          state: 'error',
          message: t(getLocalizedKeyByErrorMessage(response.data.Message)),
        });
      } else {
        setAlert({
          state: 'info',
          message: t('mfsResetPasswordSuccessMessage', { username: forgotPasswordDetails.username }),
        });
      }
    } catch (e) {
      if (e && e.data && e.data.Message) {
        setAlert({
          state: 'error',
          message: t(getLocalizedKeyByErrorMessage(e.data.Message)),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  /** method for Reset Password button click */
  const onResetPasswordClick = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    forgotPassword();
  };

  return (
    <ForgotPasswordForm
      labelKey="username"
      value={forgotPasswordDetails.username}
      placeholderKey="usernamePlaceholder"
      helperText={forgotPasswordDetails.usernameHelperText}
      alert={alert}
      locked={isAccountLocked}
      loading={loading}
      cancelRoutePath={`${routeMappings['MFS']}${routeMappings['LOGIN']}`}
      inputChangeHandler={inputChangeHandler}
      onResetPasswordClick={onResetPasswordClick}
    />
  );
};
